// Add-ons on a Storm Mode stop (migration 104).
//
// A stop picks up its customer's add-ons when it is added to the route
// (stop.addons, what is on the stop and charged). The payment modal starts
// ticked from those and the driver can untick them. stop.customer_addons is
// the customer's standing list, with the detail the owner entered ("Done
// by: 6:30 am"), shown on the stop card.
//
// Every figure here is for display. The server prices the stop from the rows
// it stores (services/pricing.js), and a percent add-on is that percent of
// snow + salt there too.

export const isPercentAddon = (addon) =>
  addon != null && addon.pricing_type === 'percent' && addon.percent != null && addon.percent !== '';

// '10', '12.5': trailing zeros dropped, as on the Settings and customer cards.
export const percentText = (pct) => String(Number(parseFloat(pct).toFixed(2)));

// A flat add-on's price for this stop: its tier price, else the Default $.
// null for a percent add-on, which has no price of its own.
export const flatAddonPrice = (addon, stop) => {
  if (isPercentAddon(addon)) return null;
  const tierId = stop?.pricing_tier_id || stop?.lawn_tier_id;
  const tierPrice = tierId && addon.tier_prices
    ? addon.tier_prices.find(tp => tp.pricing_tier_id === tierId)?.price
    : undefined;
  return tierPrice !== undefined ? parseFloat(tierPrice) || 0 : parseFloat(addon.price) || 0;
};

// "+10%" or "+$15.00", beside the add-on's tick box.
export const addonPriceLabel = (addon, stop) => (
  isPercentAddon(addon) ? `+${percentText(addon.percent)}%` : `+$${flatAddonPrice(addon, stop).toFixed(2)}`
);

// The ticked add-ons' dollars: flat prices plus each percent of snow + salt,
// rounded to the cent.
export const addonsAmount = (availableAddons, selected, stop, snowPlusSalt) => {
  const total = (availableAddons || [])
    .filter(a => selected[a.id])
    .reduce((sum, a) => sum + (isPercentAddon(a)
      ? (snowPlusSalt || 0) * parseFloat(a.percent) / 100
      : flatAddonPrice(a, stop)), 0);
  return Math.round(total * 100) / 100;
};

// The modal's starting ticks: the add-ons on the stop.
export const stopAddonSelection = (stop) => {
  const map = {};
  (Array.isArray(stop?.addons) ? stop.addons : []).forEach(a => {
    if (a && a.addonId != null) map[a.addonId] = true;
  });
  return map;
};

// The `addons` to send with the stop update, or undefined when the ticks are
// the stop's own, so an unchanged stop keeps the add-on rows (and the prices)
// it already holds. A changed list is sent whole, and an empty one clears the
// stop. An add-on on the stop that the modal does not list (switched off in
// Settings since) stays ticked and is sent with its stored price.
export const stopAddonsPayload = (availableAddons, selected, stop) => {
  const onStop = Array.isArray(stop?.addons) ? stop.addons : [];
  const ticked = Object.keys(selected).filter(id => selected[id]).map(Number);
  const before = onStop.map(a => Number(a.addonId));
  const same = ticked.length === new Set(before).size && ticked.every(id => before.includes(id));
  if (same) return undefined;
  const byId = new Map((availableAddons || []).map(a => [Number(a.id), a]));
  return ticked.map(id => {
    const addon = byId.get(id);
    if (!addon) {
      const stored = onStop.find(a => Number(a.addonId) === id);
      return { addonId: id, priceCharged: stored ? stored.priceCharged : 0 };
    }
    // A percent add-on sends no price: the server freezes its percent.
    return isPercentAddon(addon) ? { addonId: id } : { addonId: id, priceCharged: flatAddonPrice(addon, stop) };
  });
};

// The stop card line: "Walkway", "Done by: 6:30 am". An add-on with a
// detail reads by its detail label, as the driver needs it.
export const customerAddonLines = (stop) => (
  (Array.isArray(stop?.customer_addons) ? stop.customer_addons : []).map(a => (
    a.detail ? `${a.detailLabel || a.name}: ${a.detail}` : a.name
  ))
);
